// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FancyScroll, { FancyScrollRefProps } from "@guestsupply/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@guestsupply/App/Components/MiniCart/MiniCart.helper";
import MiniCartLine from "@guestsupply/App/Components/MiniCart/MiniCartLine";
import Notify from "@guestsupply/App/Components/Notify";
import { xisCheckoutDisabled } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { color, modalButtonsActionStyles, styledScrollbar } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canCheckoutWithCart,
    canSubmitRequisition,
    getCurrentCartState,
    isCartEmpty,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

interface OwnProps {
    isPopupShown?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;

    return {
        isCheckoutDisabled: xisCheckoutDisabled(cart),
        canSubmitRequisition: canSubmitRequisition(cart),
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isPunchOutOrder: cart?.properties?.isPunchout?.toLowerCase() === "true",
        totalCountDisplay: cart?.totalCountDisplay || 0,
        orderSubTotalDisplay: cart?.orderSubTotalDisplay,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        checkoutPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        cart,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isUpdatingCart,
        isLoading: isCartLoading,
        isCartEmpty: isCartEmpty(cart),
    };
};

const mapDispatchToProps = {
    doPunchOutCheckout,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    submitRequisition,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface MiniCartStyles {
    cartLinesWrapper?: InjectableCss;
    cartLinesHeader?: InjectableCss;
    itemDescription?: InjectableCss;
    qty?: InjectableCss;
    price?: InjectableCss;
    total?: InjectableCss;
    cartLineHeaderColumnText?: TypographyProps;
    summarySectionWrapper?: InjectableCss;
    miniCartInfoItem?: SmallHeadingAndTextStyles;
    infoWrapper?: InjectableCss;
    label?: TypographyProps;
    value?: TypographyProps;
    actionWrapper?: InjectableCss;
    viewCartButton?: ButtonPresentationProps;
    checkoutButton?: ButtonPresentationProps;
    centerWrapper?: InjectableCss;
    cantCheckoutCart?: InjectableCss;
    notify?: TypographyProps;
    emptyCartWrapper?: InjectableCss;
    emptyCartText?: TypographyProps;
    fancyScrollStyle?: InjectableCss;
    // XNG-Changes
    miniCartPager?: InjectableCss;
    minCartContentWrapper?: InjectableCss;
}

export const miniCartStyles: MiniCartStyles = {};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    cart,
    canCheckoutWithCart,
    canSubmitRequisition,
    cartPageUrl,
    checkoutPageUrl,
    doPunchOutCheckout,
    isCheckoutDisabled,
    isLoading,
    isPunchOutOrder,
    isUpdatingCart,
    isUpdatingCartLine,
    orderSubTotalDisplay,
    preloadCheckoutShippingData,
    requisitionConfirmationPageUrl,
    totalCountDisplay,
    isPopupShown,
    removeCartLine,
    updateCartLine,
    isCartEmpty,
    submitRequisition,
}) => {
    const history = useHistory();
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();
    // XNG-Changes
    const [pagerStart, setPagerStart] = useState(1);
    const [pagerEnd, setPagerEnd] = useState(1);

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    // XNG-Changes: adding minicart onscroll pager
    useEffect(() => {
        if (!isLoading && isPopupShown) {
            setTimeout(() => {
                handleOnScroll();
            }, 100);
        }
    }, [isLoading, isPopupShown]);

    // XNG-Changes: adding minicart onscroll pager
    const handleOnScroll = () => {
        if (!parentBoxRef.current) {
            return;
        }
        const cartLinesContainer = parentBoxRef.current.getBoundingClientRect();
        const cartLines = [...parentBoxRef.current.children];
        const visible: number[] = [];
        cartLines.forEach((cartLine, index) => {
            const currentCartline = cartLine.getBoundingClientRect();
            if (
                index % 2 &&
                currentCartline.bottom >= cartLinesContainer.top &&
                currentCartline.top <= cartLinesContainer.bottom
            ) {
                visible.push((index + 1) / 2);
            }
        });
        setPagerStart(visible[0]);
        setPagerEnd(visible[visible.length - 1]);
    };

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };
    // XNG-CHANGES: Using history.push because preloadCheckoutShippingData sometimes returns empty shipping data for guest checkout
    const checkoutHandler = () => {
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }
        setIsCheckoutLoading(true);
        history.push(checkoutPageUrl!);
        setIsCheckoutLoading(false);
        // preloadCheckoutShippingData({
        //     onSuccess: () => {
        //         setIsCheckoutLoading(false);
        //         history.push(checkoutPageUrl!);
        //     },
        // });
    };

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const handleViewCartClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        hideTippy();
        history.push(cartPageUrl);
    };

    if (!cart && isLoading) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!cart) {
        return null;
    }

    const cartLines = cart.cartLines ?? [];

    if (!cartLines?.length || isCartEmpty) {
        return (
            <StyledWrapper {...styles.emptyCartWrapper}>
                <Notify
                    {...styles.notify}
                    type="info"
                    data-test-selector="cartlineHeader_warningSomeCartLinesNotPriced"
                >
                    {siteMessage("GUEST_Cart_Empty")}
                </Notify>
            </StyledWrapper>
        );
    }

    const miniCartHeader = (
        <>
            <StyledWrapper {...styles.itemDescription}>
                <Typography {...styles.cartLineHeaderColumnText}>{translate("Item Description")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.qty}>
                <Typography {...styles.cartLineHeaderColumnText}>{translate("QTY")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.price}>
                <Typography {...styles.cartLineHeaderColumnText}>{translate("Price/Unit")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.total}>
                <Typography {...styles.cartLineHeaderColumnText}>{translate("Extended Price")}</Typography>
            </StyledWrapper>
        </>
    );

    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive);

    return (
        <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef} extendedStyles={styles.fancyScrollStyle}>
            <StyledWrapper ref={parentBoxRef} {...styles.minCartContentWrapper}>
                <LoadingOverlay loading={isLoading || isUpdatingCart || isUpdatingCartLine}>
                    {cart.properties["someCartLinesNotPriced"] === "true" && (
                        <StyledWrapper {...styles.cantCheckoutCart}>
                            <Notify
                                {...styles.notify}
                                type="warning"
                                data-test-selector="cartlineHeader_warningSomeCartLinesNotPriced"
                            >
                                {siteMessage(
                                    "Guest_CartError_Some_Items_Not_Priced",
                                    "Some of the items in your shopping cart could not be priced. Please remove these items or save your order and try again later.",
                                )}
                            </Notify>
                        </StyledWrapper>
                    )}
                    {cart.properties["someCartLinesNotValidForWarehouse"] === "true" && (
                        <StyledWrapper {...styles.cantCheckoutCart}>
                            <Notify
                                {...styles.notify}
                                type="warning"
                                data-test-selector="cartlineHeader_warningSomeCartLinesNotValidForWarehouse"
                            >
                                {siteMessage(
                                    "Guest_CartError_Some_CartLines_Not_Valid_For_Warehouse",
                                    "Some of the items in your shopping cart are not available to be shipped to your postal code. Please remove these items to continue.",
                                )}
                            </Notify>
                        </StyledWrapper>
                    )}
                    {productsCannotBePurchased && (
                        <StyledWrapper {...styles.cantCheckoutCart}>
                            <Notify
                                {...styles.notify}
                                type="danger"
                                data-test-selector="cartlineHeader_warningProductsCannotBePurchased"
                            >
                                {siteMessage("Cart_ProductsCannotBePurchased")}
                            </Notify>
                        </StyledWrapper>
                    )}
                    {cartLines.length > 0 && (
                        <>
                            {/* XNG-Changes: adding minicart onscroll pager */}
                            {/* <StyledWrapper {...styles.miniCartPager}>
                                {translate("Showing ")}
                                <b>
                                    {pagerStart}-{pagerEnd}
                                </b>
                                {translate(" out of ")}
                                <b>{cartLines.length}</b>
                            </StyledWrapper> */}
                            <StyledWrapper {...styles.cartLinesHeader}>{miniCartHeader}</StyledWrapper>
                            <StyledWrapper
                                {...styles.cartLinesWrapper}
                                // ref={parentBoxRef}
                                data-test-selector="cartLines-container"
                                onScroll={handleOnScroll}
                            >
                                {cartLines.map(cartLine => {
                                    const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                                    return (
                                        <MiniCartLine
                                            key={`miniCartLine_${cartLine.id}`}
                                            cartLine={cartLine}
                                            cart={cart}
                                            hideTippy={hideTippy}
                                            qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                            removeCartLineClickHandler={removeCartLineClickHandler}
                                            errorMessages={errorMessages}
                                        />
                                    );
                                })}
                            </StyledWrapper>
                            <StyledWrapper {...styles.summarySectionWrapper}>
                                <StyledWrapper {...styles.infoWrapper}>
                                    <SmallHeadingAndText
                                        extendedStyles={styles.miniCartInfoItem}
                                        heading={translate("Items")}
                                        text={cartLines.length}
                                    />
                                    <SmallHeadingAndText
                                        extendedStyles={styles.miniCartInfoItem}
                                        heading={translate("Subtotal")}
                                        text={`${orderSubTotalDisplay}`}
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.actionWrapper}>
                                    <Button
                                        {...styles.viewCartButton}
                                        onClick={handleViewCartClick}
                                        aria-label={translate("View Cart")}
                                    >
                                        {translate("View Cart")}
                                    </Button>
                                    {/* XNG-CHANGES : GUEST-2716 :Removing Punchount from Mini Cart */}
                                    <>
                                        {!canSubmitRequisition ? (
                                            !isPunchOutOrder && (
                                                <Button
                                                    {...styles.checkoutButton}
                                                    onClick={checkoutHandler}
                                                    disabled={
                                                        isCheckoutDisabled || isCheckoutLoading || !canCheckoutWithCart
                                                    }
                                                    aria-label={`${
                                                        cart?.requiresApproval
                                                            ? translate("Checkout for Approval")
                                                            : translate("Checkout")
                                                    }`}
                                                >
                                                    {cart?.requiresApproval
                                                        ? translate("Checkout for Approval")
                                                        : translate("Checkout")}
                                                </Button>
                                            )
                                        ) : (
                                            <Button
                                                {...styles.checkoutButton}
                                                onClick={submitRequisitionClickHandler}
                                                data-test-selector="cartTotal_SubmitRequisition"
                                                aria-label="Submit Requisition"
                                                disabled={!canCheckoutWithCart}
                                            >
                                                {translate("Submit Requisition")}
                                            </Button>
                                        )}
                                    </>
                                </StyledWrapper>
                            </StyledWrapper>
                        </>
                    )}
                </LoadingOverlay>
            </StyledWrapper>
        </FancyScroll>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MiniCart);
